import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
  DialogActions,
  Button,
  Typography,
  CircularProgress
} from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { LoadingButton } from '~/modules/common/components';
import { useValidateInvoiceAmountLimit } from '~/modules/billing-invoicing/bill/components/BillDetails/components/hooks/';
import FailedInvoiceDetails from '../BillingTransactionTab/components/FailedInvoiceDetails';
import { useBillIssueDialogOpen } from './hooks';

const useStyles = makeStyles(theme => ({
  billDisplayTextLabel: {
    fontWeight: 500
  },
  errorMessageListWrapper: {
    background: theme.palette.error.lightBackground,
    color: '#560e06',
    padding: theme.spacing(2)
  },
  errorMessageList: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    listStyleType: 'disc'
  },
  header: {
    fontWeight: 'bold',
    color: 'black'
  },
  description: {
    paddingBottom: theme.spacing(1.5),
    color: 'black'
  },
  syncMessageText: {
    fontWeight: 500
  },
  spinnerWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 150
  },
  spinner: {
    color: theme.palette.primary.main
  },
  progress: {
    paddingLeft: theme.spacing(2)
  }
}));

const useDialogStyles = makeStyles(theme => ({
  paper: {
    [theme.breakpoints.up('sm')]: {
      minWidth: '520px'
    }
  }
}));

const useDialogActionStyles = makeStyles(theme => ({
  root: { paddingRight: theme.spacing(2.5) }
}));

const getResourceKeys = confirmationDialogName => ({
  header: `billDetails.${confirmationDialogName}Dialog.${confirmationDialogName}Bill`,
  details: `billDetails.${confirmationDialogName}Dialog.details`,
  description: `billDetails.${confirmationDialogName}Dialog.description`,
  cancel: `billDetails.${confirmationDialogName}Dialog.cancel`,
  actionButton: `billDetails.${confirmationDialogName}Dialog.actionButton`
});

const InProgress = ({ classes }) => {
  return (
    <div className={classes.spinnerWrapper}>
      <CircularProgress size={40} className={classes.spinner} />
    </div>
  );
};

InProgress.propTypes = {
  classes: PropTypes.object
};

export const BillIssueConfirmationDialog = ({
  billId,
  open,
  onClose,
  confirmationDialogName,
  handleIssuePrintClick,
  handleIssueEmailClick,
  handleIssueSyncClick,
  loading,
  billDisplayText,
  failedLineItems,
  setFailedLineItems
}) => {
  const {
    data,
    loading: isValidationInProgress
  } = useValidateInvoiceAmountLimit(billId);

  useEffect(() => {
    if (!loading && data?.validateInvoiceAmountLimit?.failedLineItems) {
      setFailedLineItems(data.validateInvoiceAmountLimit.failedLineItems);
    }
  }, [loading, data, setFailedLineItems]);

  const classes = useStyles();
  const dialogStyle = useDialogStyles();
  const dialogActionClasses = useDialogActionStyles();
  const failedLineItemsCount = useMemo(
    () => ({ count: failedLineItems.length }),
    [failedLineItems]
  );
  const resourceKeys = useMemo(() => getResourceKeys(confirmationDialogName), [
    confirmationDialogName
  ]);

  const { handleClose, handleClick } = useBillIssueDialogOpen({
    handleIssuePrintClick,
    handleIssueEmailClick,
    handleIssueSyncClick,
    confirmationDialogName,
    onClose,
    setFailedLineItems
  });

  return (
    <Dialog
      classes={dialogStyle}
      disableBackdropClick
      disableEscapeKeyDown
      maxWidth="sm"
      fullScreen={useMediaQuery(theme => theme.breakpoints.down('sm'), {
        noSsr: true
      })}
      open={open}
      onClose={onClose}
      TransitionProps={useMemo(() => ({ role: 'presentation' }), [])}
      aria-labelledby="entity-confirmation-dialog-title"
      aria-describedby="entity-confirmation-dialog-description"
    >
      <DialogTitle id="entity-confirmation-dialog-title">
        <FormattedMessage className={classes.header} id={resourceKeys.header} />
      </DialogTitle>
      <DialogContent id="entity-confirmation-dialog-description">
        {isValidationInProgress ? (
          <InProgress classes={classes} />
        ) : failedLineItems.length > 0 ? (
          <div className={classes.errorMessageListWrapper}>
            <Typography
              variant="body1"
              className={classes.billDisplayTextLabel}
            >
              {billDisplayText}
            </Typography>
            <ul className={classes.errorMessageList}>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="billingTransactionList.failedBillIssueMessage"
                    values={failedLineItemsCount}
                  />
                </Typography>
              </li>
            </ul>
            <FailedInvoiceDetails details={failedLineItems} />
          </div>
        ) : (
          <>
            <Typography variant="body2" className={classes.description}>
              <FormattedMessage id={resourceKeys.details} />
            </Typography>
            <Typography variant="body2" className={classes.description}>
              <FormattedMessage id={resourceKeys.description} />
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions classes={dialogActionClasses}>
        <Button
          onClick={handleClose}
          disabled={loading || isValidationInProgress}
        >
          <FormattedMessage id={resourceKeys.cancel} />
        </Button>
        <LoadingButton
          onClick={handleClick}
          disabled={
            loading || isValidationInProgress || failedLineItems.length > 0
          }
          color="primary"
          autoFocus
          isLoading={loading}
          data-qe-id="IssueConfirm"
        >
          <FormattedMessage id={resourceKeys.actionButton} />
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

BillIssueConfirmationDialog.propTypes = {
  billId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  confirmationDialogName: PropTypes.string,
  handleIssuePrintClick: PropTypes.func,
  handleIssueEmailClick: PropTypes.func,
  handleIssueSyncClick: PropTypes.func,
  loading: PropTypes.bool,
  billDisplayText: PropTypes.string,
  failedLineItems: PropTypes.array,
  setFailedLineItems: PropTypes.func
};

export default BillIssueConfirmationDialog;
